// File: App.js
//
// This is the "switchboard" of the app. It looks at the URL in the
// browser and decides which page to show.
//
// What you can do from here:
//   - Add a new page: import it at the top, then add a new <Route />
//     line below pointing a URL to that page component.
//   - Change which URL shows which page: edit the `path` on a <Route />.
//   - Make a page require sign-in: wrap its element in <ProtectedRoute>.
//   - Make a page admin-only: wrap it in <ProtectedRoute requireAdmin>.
//
// The <Footer /> at the bottom shows on EVERY page (it lives outside
// the <Routes>). Edit src/Footer.js to change the footer text.

import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";

// All the page components. Each one is a separate file in src/.
import LoginScreen from "./LoginScreen";
import Admin from "./Admin";
import MessagesScreen from "./MessagesScreen";
import ProtectedRoute from "./ProtectedRoute";
import Home from "./Home";
import Signup from "./signup";
import TOS from "./tos";
import INQ from "./inquirypage"
import NotFound from "./notfound"
import Resources from "./resourcepage"
import Calendar from "./calendar"
import Settings from "./settingspage"
import Footer from "./Footer"
import { initThemeDefaults, applyColors, applyRoles } from "./theme";

function App() {
  // Set up the live theme: write defaults to CSS variables on mount,
  // then listen to Firestore (config/theme) for any admin overrides.
  // The doc holds palette hex values at the top level and a nested
  // `roles` object mapping role names → color names. applyColors and
  // applyRoles update the CSS variables on document root so the whole
  // UI re-paints with no React re-render.
  useEffect(() => {
    initThemeDefaults();
    const db = getFirestore();
    const unsub = onSnapshot(
      doc(db, "config", "theme"),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const { roles, ...colors } = data;
        applyColors(colors);
        if (roles) applyRoles(roles);
      },
      (err) => {
        console.warn("Theme subscription failed:", err);
      }
    );
    return unsub;
  }, []);

  return (
    <Router>
      {/* The outer wrapper makes sure the footer sits at the bottom
          of the screen even when a page's content is short. */}
      <div className="flex flex-col min-h-screen">
        <main className="flex-1 flex flex-col">
          <Routes>
            {/* Public pages — anyone (signed in or not) can see these. */}
            <Route path="/" element={<LoginScreen />} />

            {/* Pages that need sign-in AND staff approval. */}
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesScreen /></ProtectedRoute>} />

            {/* Admins only. Non-admins who hit this URL get bounced. */}
            <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />

            {/* Other routes. */}
            <Route path="/usersettings" element = {<Settings />}/>
            <Route path="/sign-up" element={<Signup />} />
            <Route path="/tos" element={< TOS />}/>
            <Route path="/inquiry" element={<ProtectedRoute><INQ /></ProtectedRoute>} />
            <Route path="/resources" element={<ProtectedRoute> <Resources/> </ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute> <Calendar/> </ProtectedRoute>} />

            {/* Catch-all: any unknown URL shows the "not found" page. */}
            <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
          </Routes>
        </main>

        {/* Sitewide footer (copyright + credit). Always visible. */}
        <Footer />
      </div>
    </Router>
  );
}

export default App;
